import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { PackageSearch, Menu, X, Mail } from "lucide-react";
import { CONTACT_EMAIL } from "./categories";

export const roleHome = (role) => ({ merchant: "/merchant", courier: "/courier", admin: "/admin", customer: "/app" }[role] || "/app");

const NAV = [
  { to: "/categories", key: "site.nav.categories", id: "categories" },
  { to: "/how-it-works", key: "site.nav.how", id: "how" },
  { to: "/about", key: "site.nav.about", id: "about" },
  { to: "/sell", key: "site.nav.sell", id: "sell" },
  { to: "/support", key: "nav.support", id: "support" },
];

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="site-logo-link" aria-label="Drivons Market home">
      <span className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><PackageSearch className="w-5 h-5 text-white" aria-hidden="true" /></span>
      <span className="font-display font-extrabold text-lg tracking-tight text-white">Drivons<span className="text-brand">Market</span></span>
    </Link>
  );
}

function AuthButtons() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (user) {
    return <Button data-testid="nav-dashboard-btn" onClick={() => navigate(roleHome(user.role))} className="bg-brand hover:bg-brand-dark rounded-full">{t("nav.dashboard")}</Button>;
  }
  return (
    <>
      <Link to="/login" className="hidden sm:inline-flex"><Button data-testid="nav-login-btn" variant="ghost" className="text-white hover:bg-white/10 rounded-full">{t("nav.login")}</Button></Link>
      <Link to="/register" className="hidden sm:inline-flex"><Button data-testid="nav-register-btn" className="bg-brand hover:bg-brand-dark rounded-full">{t("nav.register")}</Button></Link>
    </>
  );
}

const linkCls = ({ isActive }) => `text-sm font-medium transition-colors hover:text-brand ${isActive ? "text-brand" : "text-slate-300"}`;

function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 h-16 flex items-center justify-between gap-2 sm:gap-4">
        <Logo />
        <nav aria-label={t("site.nav.label")} className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.id}-link`}>{t(n.key)}</NavLink>)}
        </nav>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <LanguageSwitcher className="text-white hover:bg-white/10" />
          <AuthButtons />
          <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10" onClick={() => setOpen(!open)}
            aria-expanded={open} aria-label={t("site.nav.menu")} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>
      {open && (
        <nav aria-label={t("site.nav.label")} className="lg:hidden border-t border-white/10 px-5 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} className={linkCls} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.id}-link`}>{t(n.key)}</NavLink>)}
          <NavLink to="/login" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-login-link">{t("nav.login")}</NavLink>
          <NavLink to="/register" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-register-link">{t("nav.register")}</NavLink>
        </nav>
      )}
    </header>
  );
}

function FooterCol({ title, links }) {
  const { t } = useTranslation();
  return (
    <div>
      <h2 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">{title}</h2>
      <ul className="space-y-2.5">
        {links.map((l) => <li key={l.to}><Link to={l.to} data-testid={l.id} className="text-sm text-slate-400 hover:text-brand transition-colors font-medium">{t(l.key)}</Link></li>)}
      </ul>
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-white/10 bg-slate-950">
      <div className="max-w-7xl mx-auto px-5 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="text-sm text-slate-400 mt-4 max-w-xs">{t("site.footer.tagline")}</p>
        </div>
        <FooterCol title={t("site.footer.explore")} links={[
          { to: "/categories", key: "site.nav.categories", id: "footer-categories-link" },
          { to: "/how-it-works", key: "site.nav.how", id: "footer-how-link" },
          { to: "/sell", key: "site.nav.sell", id: "footer-sell-link" },
        ]} />
        <FooterCol title={t("site.footer.company")} links={[
          { to: "/about", key: "site.nav.about", id: "footer-about-link" },
          { to: "/support", key: "nav.support", id: "footer-support-link" },
          { to: "/privacy", key: "site.legal.privacyTitle", id: "footer-privacy-link" },
          { to: "/terms", key: "site.legal.termsTitle", id: "footer-terms-link" },
        ]} />
        <div>
          <h2 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">{t("site.footer.contact")}</h2>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-contact-email" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-brand transition-colors break-all">
            <Mail className="w-4 h-4 shrink-0 text-brand" aria-hidden="true" /> {CONTACT_EMAIL}
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500">© {new Date().getFullYear()} Drivons Market · {t("landing.footer")}</div>
    </footer>
  );
}

export default function PublicLayout({ children, testId }) {
  return (
    <div className="public-site min-h-screen bg-slate-950 text-white overflow-x-hidden flex flex-col" data-testid={testId}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-[60] bg-brand text-white px-4 py-2 rounded-full">Skip to content</a>
      <Header />
      <main id="main" className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
